import { Ionicons } from '@expo/vector-icons';
import React, { useState, useRef, useCallback } from 'react';
import {
	TextInput as RNTextInput,
	View,
	StyleSheet,
	TouchableOpacity,
	Platform,
	Modal,
	KeyboardAvoidingView,
	ScrollView,
	type TextInputSelectionChangeEvent,
	type TextInputInstance,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { SegmentedButtons } from './segmented-buttons';
import { Text } from './text';
import type { TextInputProps } from './text-input';

import { Markdown } from '@/components/common/markdown';
import { useTheme } from '@/components/theme-provider';

type InputStyleProps = Pick<TextInputProps, 'mode' | 'error'>;

type ToolbarButton = 'bold' | 'italic' | 'list';

export interface MarkdownTextInputProps extends Omit<TextInputProps, 'enableFullScreen'> {
	showPreview?: boolean;
	showToolbar?: boolean;
	toolbarButtons?: ToolbarButton[];
}

interface Selection {
	start: number;
	end: number;
}

export const MarkdownTextInput: React.FC<MarkdownTextInputProps> = ({
	showPreview = true,
	showToolbar = true,
	toolbarButtons = ['bold', 'italic', 'list'],
	value = '',
	onChangeText,
	label,
	multiline = true,
	numberOfLines = 4,
	style,
	autoFocus = false,
	...props
}) => {
	const theme = useTheme();
	const [mode, setMode] = useState<'edit' | 'preview'>('edit');
	const [selection, setSelection] = useState<Selection>({ start: 0, end: 0 });
	const [isFullScreenVisible, setIsFullScreenVisible] = useState(false);
	const fullScreenInputRef = useRef<TextInputInstance>(null);

	const isMobile = Platform.OS !== 'web';
	const text = value?.toString() ?? '';

	const handleSelectionChange = useCallback((event: TextInputSelectionChangeEvent) => {
		setSelection(event.nativeEvent.selection);
	}, []);

	const insertMarkdown = useCallback((prefix: string, suffix: string, placeholder: string, isFullScreen = false) => {
		const { start, end } = selection;
		let newText: string;

		if (start !== end) {
			const selectedText = text.slice(start, end);
			newText = text.slice(0, start) + prefix + selectedText + suffix + text.slice(end);
		} else {
			newText = text.slice(0, start) + prefix + placeholder + suffix + text.slice(start);
		}

		onChangeText?.(newText);

		if (isFullScreen) {
			const newCursorPos = start !== end
				? start + prefix.length + (end - start) + suffix.length
				: start + prefix.length + placeholder.length;

			setTimeout(() => {
				fullScreenInputRef.current?.setNativeProps({
					selection: { start: newCursorPos, end: newCursorPos },
				});
			}, 50);
		}
	}, [selection, text, onChangeText]);

	const insertListItem = useCallback((isFullScreen = false) => {
		const { start } = selection;
		const beforeCursor = text.slice(0, start);
		const afterCursor = text.slice(start);

		const lineStart = beforeCursor.lastIndexOf('\n') + 1;
		const beforeLine = text.slice(0, lineStart);
		const currentLineBeforeCursor = text.slice(lineStart, start);

		let newText: string;
		let newCursorPos: number;

		if (currentLineBeforeCursor.trim() === '') {
			newText = beforeLine + '- ' + currentLineBeforeCursor + afterCursor;
			newCursorPos = lineStart + 2;
		} else {
			newText = beforeCursor + '\n- ' + afterCursor;
			newCursorPos = start + 3;
		}

		onChangeText?.(newText);

		if (isFullScreen) {
			setTimeout(() => {
				fullScreenInputRef.current?.setNativeProps({
					selection: { start: newCursorPos, end: newCursorPos },
				});
			}, 50);
		}
	}, [selection, text, onChangeText]);

	const handleBold = useCallback((isFullScreen = false) => {
		insertMarkdown('**', '**', 'bold text', isFullScreen);
	}, [insertMarkdown]);

	const handleItalic = useCallback((isFullScreen = false) => {
		insertMarkdown('*', '*', 'italic text', isFullScreen);
	}, [insertMarkdown]);

	const handleList = useCallback((isFullScreen = false) => {
		insertListItem(isFullScreen);
	}, [insertListItem]);

	const renderToolbar = (isFullScreen = false) => (
		<View style={[styles.toolbar, { borderColor: theme.colors.outline }]}>
			{toolbarButtons.includes('bold') && (
				<TouchableOpacity
					onPress={() => handleBold(isFullScreen)}
					style={styles.toolbarButton}
					accessibilityLabel="Bold"
				>
					<Text style={styles.toolbarButtonText}>B</Text>
				</TouchableOpacity>
			)}
			{toolbarButtons.includes('italic') && (
				<TouchableOpacity
					onPress={() => handleItalic(isFullScreen)}
					style={styles.toolbarButton}
					accessibilityLabel="Italic"
				>
					<Text style={[styles.toolbarButtonText, { fontStyle: 'italic' }]}>I</Text>
				</TouchableOpacity>
			)}
			{toolbarButtons.includes('list') && (
				<TouchableOpacity
					onPress={() => handleList(isFullScreen)}
					style={styles.toolbarButton}
					accessibilityLabel="List"
				>
					<Ionicons name="list" size={20} color={theme.colors.onSurface} />
				</TouchableOpacity>
			)}
		</View>
	);

	const handleDone = () => {
		setIsFullScreenVisible(false);
	};

	const handleFocus = () => {
		if (isMobile && Platform.OS === 'ios') {
			setIsFullScreenVisible(true);
		}
	};

	React.useEffect(() => {
		if (isMobile && Platform.OS === 'ios' && autoFocus) {
			setIsFullScreenVisible(true);
		}
	}, [isMobile, autoFocus]);

	return (
		<>
			<View style={styles.container}>
				{label && (
					<Text style={styles.label}>{label}</Text>
				)}

				{showPreview && (
					<SegmentedButtons
						buttons={[
							{ value: 'edit', label: 'Edit' },
							{ value: 'preview', label: 'Preview' },
						]}
						value={mode}
						onValueChange={(v) => setMode(v as 'edit' | 'preview')}
						style={styles.segmentedButtons}
					/>
				)}

				{mode === 'edit' ? (
					<>
						{showToolbar && renderToolbar()}
						<RNTextInput
							value={text}
							onChangeText={onChangeText}
							onSelectionChange={handleSelectionChange}
							onFocus={handleFocus}
							multiline={multiline}
							numberOfLines={numberOfLines}
							style={[styles.input, showToolbar && styles.inputWithToolbar, style]}
							autoFocus={autoFocus}
							placeholderTextColor="#757575"
							{...props}
						/>
					</>
				) : (
					<View style={[styles.previewContainer, { borderColor: theme.colors.outline }]}>
						{text ? (
							<Markdown>{text}</Markdown>
						) : (
							<Text style={styles.placeholder}>Nothing to preview</Text>
						)}
					</View>
				)}

				<Text style={styles.helperText}>
					Supports **bold**, *italic*, and - lists
				</Text>
			</View>

			{isMobile && Platform.OS === 'ios' && (
				<Modal
					visible={isFullScreenVisible}
					animationType="slide"
					presentationStyle="fullScreen"
					onRequestClose={handleDone}
				>
					<SafeAreaView style={styles.fullScreenContainer}>
						<View style={styles.fullScreenHeader}>
							<View style={{ flex: 1 }}>
								{label && <Text style={styles.fullScreenLabel}>{label}</Text>}
							</View>
							{showToolbar && renderToolbar(true)}
							<TouchableOpacity onPress={handleDone} style={styles.doneButton}>
								<Text style={styles.doneButtonText}>Done</Text>
							</TouchableOpacity>
						</View>

						{showPreview && (
							<View style={styles.fullScreenSegmented}>
								<SegmentedButtons
									buttons={[
										{ value: 'edit', label: 'Edit' },
										{ value: 'preview', label: 'Preview' },
									]}
									value={mode}
									onValueChange={(v) => setMode(v as 'edit' | 'preview')}
								/>
							</View>
						)}

						{mode === 'edit' ? (
							<KeyboardAvoidingView
								style={{ flex: 1 }}
								behavior="padding"
								keyboardVerticalOffset={60}
							>
								<ScrollView
									style={styles.fullScreenScrollView}
									contentContainerStyle={styles.fullScreenScrollContent}
									keyboardShouldPersistTaps="handled"
									keyboardDismissMode="interactive"
									automaticallyAdjustKeyboardInsets
								>
									<RNTextInput
										ref={fullScreenInputRef}
										style={styles.fullScreenInput}
										placeholderTextColor="#757575"
										multiline
										textAlignVertical="top"
										autoFocus
										scrollEnabled
										value={text}
										onChangeText={onChangeText}
										onSelectionChange={handleSelectionChange}
										{...props}
									/>
									<View style={{ height: 120 }} />
								</ScrollView>
							</KeyboardAvoidingView>
						) : (
							<ScrollView style={styles.fullScreenScrollView} contentContainerStyle={styles.fullScreenScrollContent}>
								{text ? (
									<Markdown>{text}</Markdown>
								) : (
									<Text style={styles.placeholder}>Nothing to preview</Text>
								)}
							</ScrollView>
						)}
					</SafeAreaView>
				</Modal>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	container: {
		marginVertical: 12,
	},
	label: {
		marginBottom: 6,
		fontSize: 14,
		fontWeight: '600',
		color: '#424242',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	segmentedButtons: {
		marginBottom: 8,
	},
	toolbar: {
		flexDirection: 'row',
		borderWidth: 1,
		borderBottomWidth: 0,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		backgroundColor: '#f5f5f5',
		paddingHorizontal: 4,
		paddingVertical: 4,
	},
	toolbarButton: {
		paddingHorizontal: 12,
		paddingVertical: 6,
		marginHorizontal: 2,
		borderRadius: 4,
	},
	toolbarButtonText: {
		fontSize: 16,
		fontWeight: '700',
	},
	previewContainer: {
		borderWidth: 1,
		borderRadius: 8,
		padding: 16,
		minHeight: 120,
		backgroundColor: '#ffffff',
	},
	placeholder: {
		color: '#757575',
		fontStyle: 'italic',
	},
	helperText: {
		marginTop: 6,
		fontSize: 12,
		color: '#757575',
		fontStyle: 'italic',
	},
	input: {
		backgroundColor: '#ffffff',
		color: '#212121',
		fontSize: 16,
		paddingHorizontal: 16,
		paddingVertical: 16,
		borderRadius: 8,
		minHeight: 120,
		borderWidth: 1,
		borderColor: '#e0e0e0',
		textAlignVertical: 'top',
	},
	inputWithToolbar: {
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
	},
	fullScreenContainer: {
		marginTop: Platform.OS === 'ios' ? 32 : 0,
		paddingTop: 5,
		flex: 1,
		backgroundColor: '#ffffff',
	},
	fullScreenHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 16,
		paddingVertical: 12,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
		backgroundColor: '#f8f9fa',
		gap: 8,
	},
	fullScreenLabel: {
		fontSize: 18,
		fontWeight: '600',
		color: '#212121',
	},
	fullScreenSegmented: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
	},
	doneButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		backgroundColor: '#007AFF',
		borderRadius: 8,
	},
	doneButtonText: {
		color: '#ffffff',
		fontSize: 16,
		fontWeight: '600',
	},
	fullScreenScrollView: {
		flex: 1,
	},
	fullScreenScrollContent: {
		flexGrow: 1,
		padding: 16,
	},
	fullScreenInput: {
		flex: 1,
		fontSize: 18,
		lineHeight: 26,
		color: '#212121',
		textAlignVertical: 'top',
		padding: 0,
		margin: 0,
		minHeight: 300,
	},
});
